<template>
  <Btn
    :color="color"
    :type="type"
    :size="size"
    :icon="icon"
    :disabled="isDisabled || loading"
    :class="backgroundClass"
    :shortcut="shortcut"
  >
    <LoadingIndicator v-if="loading" key="loading" />
    <slot v-if="hasSlotIcon && !loading" name="icon" />

    <template v-if="!icon">
      <template v-if="showDefaultText"><slot /></template>
      <template v-else-if="failed">{{ failedText }}</template>
      <template v-else-if="success">{{ successText }}</template>
    </template>
  </Btn>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import LoadingIndicator from '@components/common/LoadingIndicator.vue';
import { buttonColorProp } from './Btn.vue';
import { hasSlotContent } from '@components/slots';

export enum Status {
  Idle,
  Success,
  Loading,
  Failed = -1,
}

export default defineComponent({
  name: 'ButtonLoading',

  components: {
    LoadingIndicator,
  },

  props: {
    /**
     * All Btn colors are allowed, but since the success / failed states override the background color,
     * the colors without background might not make a lot of sense
     */
    color: buttonColorProp,

    /**
     * 0 - default
     * 1 - success
     * 2 - loading
     * -1 - failed
     */
    status: {
      type: Number as PropType<Status>,
      default: Status.Idle,
    },

    successText: {
      type: String,
      default: '',
      required: false,
    },

    failedText: {
      type: String,
      default: '',
      required: false,
    },

    type: {
      type: String,
      default: 'button',
      required: false,
    },

    size: {
      type: String,
      default: 'medium',
      required: false,
    },

    icon: {
      type: Boolean,
      default: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    shortcut: {
      type: [String, Object as PropType<String | { [key: string]: (event: KeyboardEvent) => void }>],
      default: () => {},
    },
  },

  computed: {
    backgroundClass(): { [key: string]: boolean } {
      return {
        'button--loader-error': this.failed,
        'button--loader-success': this.success,
        'is-loading': this.loading,
      };
    },

    idle(): boolean {
      return this.status === Status.Idle;
    },

    loading(): boolean {
      return this.status === Status.Loading;
    },

    success(): boolean {
      return this.status === Status.Success;
    },

    failed(): boolean {
      return this.status === Status.Failed;
    },

    showDefaultText(): boolean {
      return this.idle || this.loading || (this.failed && !this.failedText) || (this.success && !this.successText);
    },

    hasSlotIcon() {
      return hasSlotContent(this.$slots.icon, {});
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
.button--loader-error:not(.is-loading) {
  background-color: colors.$code-red;
  color: colors.$color-white;

  &:not(:disabled) {
    &:hover {
      background: colors.$code-red;
      color: colors.$color-white;
    }
  }
}

.button--loader-success:not(.is-loading) {
  background-color: colors.$color-green;
  color: colors.$color-white;

  &:disabled {
    color: colors.$color-grey-200;
  }

  &:not(:disabled) {
    &:hover {
      background: colors.$color-green;
      color: colors.$color-white;
    }
  }
}
</style>
